import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

const CLIENT_ID_KEY = "agentkai_client_id";
const SESSION_ID_KEY = "agentkai_session_id";
const SESSION_DISPLAY_KEY = "agentkai_session_display_id";
const CLIENT_NAME_KEY = "agentkai_client_name";
const WORKSPACE_CACHE_KEY = "agentkai_workspace_cache";

const defaultWorkspace = {
  clientId: "default",
  sessionId: "default",
  sessionDisplayId: "default",
  clientName: "default",
};

const WorkspaceContext = createContext({
  workspace: defaultWorkspace,
  setWorkspace: () => {},
  workspaces: [],
  setWorkspaces: () => {},
});

const safeSessionGet = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key, value) => {
  try {
    if (value === undefined || value === null) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, value);
    }
  } catch {
    /* ignore storage errors */
  }
};

const titleCaseFromId = (text = "") =>
  text
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const readWorkspaceFromSession = () => {
  const clientId = safeSessionGet(CLIENT_ID_KEY) || defaultWorkspace.clientId;
  const sessionId =
    safeSessionGet(SESSION_ID_KEY) || defaultWorkspace.sessionId;
  const sessionDisplayId =
    safeSessionGet(SESSION_DISPLAY_KEY) || sessionId || defaultWorkspace.sessionId;
  let clientName = safeSessionGet(CLIENT_NAME_KEY) || "";
  clientName = clientName.trim() || titleCaseFromId(clientId);

  return {
    clientId,
    sessionId,
    sessionDisplayId,
    clientName: clientName || defaultWorkspace.clientName,
  };
};

const readWorkspaceCache = () => {
  try {
    const raw = sessionStorage.getItem(WORKSPACE_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
};

export const WorkspaceProvider = ({ children }) => {
  const [workspace, setWorkspaceState] = useState(readWorkspaceFromSession);
  const [workspaces, setWorkspacesState] = useState(readWorkspaceCache);

  const setWorkspace = useCallback((partial) => {
    setWorkspaceState((prev) => {
      const next = {
        ...defaultWorkspace,
        ...prev,
        ...partial,
      };
      safeSessionSet(CLIENT_ID_KEY, next.clientId);
      safeSessionSet(SESSION_ID_KEY, next.sessionId);
      safeSessionSet(
        SESSION_DISPLAY_KEY,
        next.sessionDisplayId || next.sessionId
      );
      if (next.clientName) {
        safeSessionSet(CLIENT_NAME_KEY, next.clientName);
      }
      return next;
    });
  }, []);

  const setWorkspaces = useCallback((list) => {
    const sanitized = Array.isArray(list) ? list : [];
    setWorkspacesState(sanitized);
    try {
      sessionStorage.setItem(
        WORKSPACE_CACHE_KEY,
        JSON.stringify(sanitized)
      );
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({
      workspace,
      setWorkspace,
      workspaces,
      setWorkspaces,
    }),
    [workspace, setWorkspace, workspaces, setWorkspaces]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return ctx;
};
